<template>
  <div class="ref-program-list">
    <HeadSection
      title="Referral programs"
      :permissionOfSection="permissionOfSection"
    >
      <template #actions>
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="createProgram"
          >
            Create
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="all-filters">
        <ListFilters
          ref="filters"
          :loading="loadingPrograms"
          :disabled="!hasListData"
          @update:isFiltered="onFilterStatusChange"
          @change="(filters) => {resetPage(); getPrograms(filters)}"
        />
        <a-button
          v-if="isFiltered"
          size="large"
          :disabled="loadingPrograms"
          class="w-fit"
          @click="() => handleReset()"
        >
          Reset filters
        </a-button>
      </div>
      <a-table
        class="ref-program-table"
        :dataSource="programsListData"
        :pagination="programsListMeta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="programsColumnsComputed"
        :loading="loadingPrograms"
        @change="changePagination"
      >
        <template slot="name" slot-scope="name, record">
          <div class="link">
            <router-link class="text-primary" :to="`/referral/programs/${record.id}`">{{ name }}</router-link>
          </div>
        </template>
        <template slot="started_at" slot-scope="started_at">
          <span>{{ started_at ? formatDate(started_at) : '-' }}</span>
        </template>
        <template slot="expires_at" slot-scope="expires_at">
          <span>{{ expires_at ? formatDate(expires_at) : '-' }}</span>
        </template>
        <template slot="status" slot-scope="status">
          <a-badge
            v-if="status"
            :count="status"
            :number-style="{
              backgroundColor: programStatusColors(status).background,
              color: programStatusColors(status).color,
              boxShadow: 'none',
            }"
            style="transform: scale(1.2);"
          />
          <span v-else>—</span>
        </template>
      </a-table>
      <TotalCount :count="programsListMeta.total" />
    </MainContainer>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import ListFilters from '@/views/apps/referral-program/referral-programs/list/listFilters.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { waitForRef } from '@/composables/useWaitForRef.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getProgramStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useProgramStatuses.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    ListFilters,
    TotalCount,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    filters: {
      page: undefined,
      per_page: undefined,
      sort_field: undefined,
      sort_dir: undefined,
    },
    listFilters: false,
    programsListData: [],
    programsListMeta: {
      current: 1,
      per_page: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    loadingPrograms: false,
    programsColumns: [
      {
        title: 'ID',
        dataIndex: 'id',
        width: '10%',
        scopedSlots: { customRender: 'id' },
      },
      {
        title: 'Referral programm',
        dataIndex: 'name',
        width: '20%',
        ellipsis: true,
        scopedSlots: {
          customRender: 'name',
        },
      },
      {
        title: 'Start date',
        dataIndex: 'started_at',
        width: '20%',
        scopedSlots: { customRender: 'started_at' },
      },
      {
        title: 'End date',
        dataIndex: 'expires_at',
        width: '20%',
        scopedSlots: { customRender: 'expires_at' },
      },
      {
        title: 'Members',
        dataIndex: 'members',
        width: '15%',
        sorter: true,
        sortOrder: undefined,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: '15%',
        scopedSlots: { customRender: 'status' },
      },
    ],
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '') || this.listFilters
    },
    isHasListItems() {
      return !!this.programsListData?.length
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
    programsColumnsComputed() {
      return this.programsColumns.map((col) => {
        if (col.dataIndex === 'members') {
          return {
            ...col,
            sortOrder:
              this.filters.sort_field === 'members_count'
                ? this.filters.sort_dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : false,
          }
        }
        return col
      })
    },
  },
  methods: {
    customRow(referral) {
      return {
        on: {
          click: () => {
            this.$router.push(`/referral/programs/${referral.id}`)
          },
        },
      }
    },
    createProgram() {
      this.$router.push('/referral/programs/create')
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY HH:mm')
    },
    programStatusColors(status) {
      return getProgramStatusColors(status)
    },
    resetPage() {
      this.filters.page = 1
    },
    onFilterStatusChange(val) {
      this.listFilters = val
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      if (sorter && sorter?.order) {
        this.filters.sort_field = 'members_count'
        this.filters.sort_dir = sorter.order === 'ascend' ? 'asc' : 'desc'
      } else {
        this.filters.sort_field = undefined
        this.filters.sort_dir = undefined
      }
      this.getPrograms()
    },
    async getFilters() {
      const { date_start, date_end, status, name } = await waitForRef('filters', this)
        .then((filtersRef) => {
          return filtersRef.getFilters()
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })

      const filters = {
        ...this.filters,
        date_start,
        date_end,
        status,
        name,
      }

      setQueryParams(filters, this, '', true)

      return getFilledFilters({ filters, dateFields: ['date_start', 'date_end'] })
    },
    async getPrograms() {
      try {
        this.loadingPrograms = true

        const filters = await this.getFilters()

        const url = `${referralApiUrl}/referral-programs`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.programsListData = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.programsListMeta, newMeta)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loadingPrograms = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.$refs['filters'].resetFilters()
      this.getPrograms()
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, '', true)
      await waitForRef('filters', this)
        .then((filtersRef) => {
          filtersRef.setQueryFilters(this.$route.query)
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })
    }
    this.getPrograms()
  },
}
</script>

<style lang="scss">
.ref-program-list {
  .all-filters {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    margin-bottom: 10px;
  }
}
.ref-program-table {
  .link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    &:hover {
      text-decoration: underline;
    }
  }
  .ant-badge > sup {
    text-transform: capitalize;
  }
  .ant-table-row {
    cursor: pointer;
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
